import { SubmissionContext, useForm } from 'vee-validate';
import { useAjv } from '@/validation/ajv';
import { createValidationSchema } from '@/validation/createSchemaValidator';
import type { FormMode, FormProps } from './types';
import { useFormConfirmationModal } from '../confirmationModals/useFormConfirmationModal';
import { computed, onBeforeUnmount, provide, ref, Ref, toValue, type InjectionKey } from 'vue';
import { useActionWithConfirmation } from '../confirmationModals/useActionWithConfirmation';
import { useTranslation } from 'i18next-vue';

export type VeeFormContext = ReturnType<typeof useVeeForm>['form'];

/**
 * extend `useForm()` from VeeValidate, so that we can provide the JSON-schema in the form context
 */
const { getSchema, initialize } = useAjv();
initialize();

export const veeFormInjectionKey: InjectionKey<VeeFormContext> = Symbol();

export const formModeInjectionKey: InjectionKey<Ref<FormMode>> = Symbol();
export const uploadingStateInjectionKey: InjectionKey<{
  uploadsRunning: Ref<Set<string>>;
  addUploadRunning: (fieldName: string) => void;
  removeUploadRunning: (fieldName: string) => void;
  uploadErrors: Ref<Set<string>>;
  addUploadError: (fieldName: string) => void;
  removeUploadError: (fieldName: string) => void;
}> = Symbol();

export const useVeeForm = <TValues extends Record<string, any>>({
  schemaName,
  options,
  confirmationOptions,
  mode,
  customValidateFunctions,
  debounceWait,
  validationPriority,
}: FormProps<TValues>) => {
  // We need this because, because the the VeeFormFields will trigger a new validation-run when unmounting because of `useFieldValue()`
  const skipValidation = ref(false);
  onBeforeUnmount(() => {
    skipValidation.value = true;
  });

  // upload state management - Map-based strategy to track fields currently uploading
  const uploadsRunning = ref(new Set<string>());

  const addUploadRunning = (fieldName: string) => {
    uploadsRunning.value.add(fieldName);
  };

  const removeUploadRunning = (fieldName: string) => {
    uploadsRunning.value.delete(fieldName);
  };

  const uploadErrors = ref(new Set<string>());

  const addUploadError = (fieldName: string) => {
    uploadErrors.value.add(fieldName);
  };

  const removeUploadError = (fieldName: string) => {
    uploadErrors.value.delete(fieldName);
  };

  const validationSchema =
    (schemaName || customValidateFunctions) &&
    createValidationSchema({
      validateFunction: schemaName ? getSchema(schemaName) : undefined,
      customValidateFunctions,
      uploadsRunning,
      uploadErrors,
      debounceWait,
      skipValidation,
      validationPriority: validationPriority ?? 'prioritizeSchemaValidation',
      options,
    });

  const { handleSubmit: formHandleSubmit, ...form } = useForm({
    ...options,
    validationSchema,
  });

  type OnSubmitCb = Parameters<typeof formHandleSubmit>[0];
  type OnSubmitValidationErrorCb = Parameters<typeof formHandleSubmit>[1];
  interface HandleSubmitOptions {
    withReferenceCheck?: boolean;
    onSubmitValidationErrorCb?: OnSubmitValidationErrorCb;
  }

  let handleSubmitCallback: OnSubmitCb;
  const { t } = useTranslation();

  const { action } = useActionWithConfirmation({
    confirmedAction: async ([values, ctx]: [values: TValues, ctx: SubmissionContext<TValues>]) => {
      await handleSubmitCallback(values, ctx);
    },
    confirmationOptions: {
      title: t('Confirmation'),
      confirmButton: t('SaveAndClose'),
      confirmationButtonType: 'Save',
      text: t('CommonConfirmationForEntitiesWithRefMsg'),
    },
  });

  // We use the origin `handleSubmitCallback`, but optionally extend it with a Dialog-Check
  const extendedHandleSubmit = (
    _handleSubmitCallback: OnSubmitCb,
    { withReferenceCheck, onSubmitValidationErrorCb }: HandleSubmitOptions = {},
  ) => {
    handleSubmitCallback = _handleSubmitCallback;
    // use async/await to make `isSubmitting` working
    return formHandleSubmit(async (values, ctx) => {
      if (form.values.isReferenced && withReferenceCheck && form.meta.value.dirty) {
        await action({ param: [values, ctx] });
      } else {
        await handleSubmitCallback(values, ctx);
      }
    }, onSubmitValidationErrorCb);
  };

  // Provide upload state to child components
  provide(uploadingStateInjectionKey, {
    addUploadRunning,
    removeUploadRunning,
    uploadsRunning,
    uploadErrors,
    addUploadError,
    removeUploadError,
  });

  provide(
    formModeInjectionKey,
    computed(() => toValue(mode)),
  );

  const extendedForm = { ...form, handleSubmit: extendedHandleSubmit };
  provide(veeFormInjectionKey, extendedForm);

  return {
    form: extendedForm,
    ...(confirmationOptions?.skipDirtyCheck === 'always'
      ? {}
      : useFormConfirmationModal<TValues>(form.meta, form.isSubmitting, confirmationOptions)),
  };
};
